import { makeAutoObservable } from 'mobx';
import { SubscriptionStatus } from '../enums/subscriptionStatus';
import { PackageListService } from '../services/api/package-list';

export enum PackageState {
  PendingOTT = 'PendingOTT',
  PendingOTTActiveDTH = 'PendingOTTActiveDTH',
  PendingOTTInactiveDTH = 'PendingOTTInactiveDTH',
  ActiveEligible = 'ActiveEligible',
  InactiveIneligible = 'InactiveEligible',
  EligibleSwitch = 'EligibleSwitch',
  None = 'None',
}

export class PackageStore {
  isLoading!: boolean;
  subscriptions!: any;
  state!: PackageState;
  currentSubscription!: any;
  pendingSubscription!: any;

  constructor() {
    makeAutoObservable(this);
  }

  initialise(country: string, customerNumber: string) {
    this.changeIsLoading(true);
    return PackageListService.getSubscriptionsWithContent(customerNumber, country).then(
      ({ content, flexSubscriptions, subscriptions }) => {
        if (subscriptions) {
          this.changeSubscriptions(subscriptions);
          const availableSubscription = subscriptions.filter(
            (subscription: any) =>
              subscription.agreementType === 'General' && subscription.isPrimarySc && subscription.services?.length
          );
          const availableOTTSubscription = subscriptions
            .concat(flexSubscriptions)
            .filter((subscription: any) => subscription.agreementType === 'OTT' && subscription.services);

          const availablePackages = PackageStore.ReduceSubscriptionsToServices(
            availableSubscription,
            content,
            'General'
          );
          const availableOTTPackages = PackageStore.ReduceSubscriptionsToServices(
            availableOTTSubscription,
            content,
            'OTT'
          );

          const activePendingOTT = availableOTTPackages.filter(
            (service: any) =>
              service.status === SubscriptionStatus.AwaitingPayment || service.status === SubscriptionStatus.Pending
          );

          if (activePendingOTT.length) {
            this.changePendingSubscription(activePendingOTT);
            this.changeState(PackageState.PendingOTT);
          }

          const activeEligible = [
            ...availablePackages,
            ...availableOTTPackages.filter(
              (service: any) =>
                service.status !== SubscriptionStatus.AwaitingPayment && service.status !== SubscriptionStatus.Pending
            ),
          ].filter(
            (service: any) =>
              service.status !== SubscriptionStatus.DisconnectPendingReconnect &&
              service.status !== SubscriptionStatus.Disconnected &&
              service.status !== SubscriptionStatus.RequestDisconnect
          );

          if (activeEligible?.length) {
            this.changeCurrentSubscription(activeEligible);
            this.changeIsLoading(false);
            this.changeState(
              this.state === PackageState.PendingOTT ? PackageState.PendingOTTActiveDTH : PackageState.ActiveEligible
            );
            return;
          }

          const inactiveEligible = [
            ...availablePackages,
            ...availableOTTPackages.filter(
              (service: any) =>
                service.status !== SubscriptionStatus.AwaitingPayment && service.status !== SubscriptionStatus.Pending
            ),
          ].filter(
            (service: any) =>
              service.status === SubscriptionStatus.DisconnectPendingReconnect ||
              service.status === SubscriptionStatus.Disconnected ||
              service.status === SubscriptionStatus.RequestDisconnect
          );
          if (inactiveEligible?.length) {
            this.changeCurrentSubscription(inactiveEligible);
            this.changeIsLoading(false);
            this.changeState(
              this.state === PackageState.PendingOTT ? PackageState.PendingOTTInactiveDTH : PackageState.EligibleSwitch
            );
            return;
          }

          this.changeState(this.state === PackageState.PendingOTT ? PackageState.PendingOTT : PackageState.None);
          this.changeIsLoading(false);
          return;
        }
        this.changeState(PackageState.None);
        this.changeIsLoading(false);
      }
    );
  }

  static ReduceSubscriptionsToServices(availableSubscription: any[], content: any[], agreementType: 'General' | 'OTT') {
    return availableSubscription.reduce((prev: any[], curr: any) => {
      return [
        ...prev,
        ...curr.services
          .filter(
            (service: any) =>
              service.agreementType === agreementType &&
              (service.type === 'Principal' ||
                (agreementType === 'OTT' && (service.type === 'Short Term Subscription' || service.type === 'Flex'))) &&
              !prev.find((item) => item.agreementId === service.agreementId)
          )
          .map((service: any) => {
            const matchingContent =
              content.find((item: any) => `${item.Code}`.toLowerCase() === `${service.serviceName}`.toLowerCase()) ||
              {};
            const extraStreamService = curr.services.find((service: any) => service.serviceName === 'STREAM MOBILE');
            return {
              ...service,
              smartcardNumber: curr.smartcardNumber,
              extraStreamAmount: matchingContent?.ExtraStreamPrice,
              code: matchingContent?.Code,
              iconCode: matchingContent?.IconCode,
              packageColor: matchingContent?.PackageColor,
              secondaryPackageColor: matchingContent?.SecondaryPackageColor,
              includesStream: matchingContent?.IncludesStream,
              packageExcerpt: matchingContent?.PackageExcerpt,
              bundleExcerpt: matchingContent?.BundleExcerpt,
              termsAndConditionDisclaimer: matchingContent?.TermsAndConditionDisclaimer,
              hasExtraStream: extraStreamService !== undefined,
              extraStreamId: extraStreamService?.productId,
              streamQuantity: extraStreamService?.quantity,
            };
          }),
      ];
    }, []);
  }

  changeState(state: PackageState) {
    this.state = state;
  }

  changeIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  changeSubscriptions(subscriptions: any) {
    this.subscriptions = subscriptions;
  }

  changeCurrentSubscription(currentSubscription: any) {
    this.currentSubscription = currentSubscription;
  }

  changePendingSubscription(pendingSubscription: any) {
    this.pendingSubscription = pendingSubscription;
  }
}
